import React, { useState, useEffect, useRef, useCallback } from "react";
import { patchWord, patchSense, createSense, deleteSense, setPrimarySense, patchPlural, addPlural, deletePlural, linkSingular, searchWords, fetchDefinitionFromDictionary, fetchDefinitionFromAI, setWordConjugationLink, patchTopicWord, setWordRoot, addSenseExtraForm, patchSenseExtraForm, deleteSenseExtraForm, fetchWordDetail, addSenseParticle, patchSenseParticle, deleteSenseParticle, mergeWord } from "../api";
import ParadigmSheet, { tagsToLabel, groupConjugations } from "./ParadigmSheet";
import WordMorphology from "./WordMorphology";

const WORD_SEARCH_LIMIT = 20;

// Strip Arabic diacritics + tatweel for comparing words ignoring harakat.
const _ARABIC_HARAKAT_RE = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;
const normForMatch = (s) => (s || "").replace(_ARABIC_HARAKAT_RE, "").trim();

// ── Shared small components ────────────────────────────────────────────────

function PencilButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
      title="Edit"
      aria-label="Edit"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z"
        />
      </svg>
    </button>
  );
}

function TrashButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-1 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
      title="Delete"
      aria-label="Delete"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    </button>
  );
}

function EditActions({ onSave, onCancel, saving, error }) {
  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="px-3 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {saving ? "Saving\u2026" : "Save"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="px-3 py-1 text-xs rounded-md border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-700 disabled:opacity-50"
      >
        Cancel
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

// ── Word selector (search-as-you-type dropdown) ────────────────────────────
// Used in SenseEdit to pick verb-form linked words by Arabic text instead of ID.

function WordSelector({ label, initialWord, onSelect }) {
  const [query, setQuery] = useState(initialWord?.text || "");
  const [results, setResults] = useState([]);
  const [selectedWord, setSelectedWord] = useState(initialWord || null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = async (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedWord(null);
    onSelect(null);

    if (val.trim() === "") {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const words = await searchWords(val.trim());
      setResults(words.slice(0, WORD_SEARCH_LIMIT));
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (word) => {
    setSelectedWord(word);
    setQuery(word.text);
    setResults([]);
    setOpen(false);
    onSelect(word.id);
  };

  const handleClear = () => {
    setSelectedWord(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    onSelect(null);
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
      <div className="flex gap-1">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          dir="rtl"
          lang="ar"
          placeholder="Search Arabic word…"
          className={`flex-1 px-2 py-1 rounded border text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
            selectedWord
              ? "border-emerald-400 bg-emerald-50"
              : "border-slate-300"
          }`}
        />
        {(query || selectedWord) && (
          <button
            type="button"
            onClick={handleClear}
            className="px-2 py-1 rounded border border-slate-300 text-xs text-slate-500 hover:bg-slate-100"
            title="Clear"
          >
            ✕
          </button>
        )}
      </div>

      {loading && (
        <p className="text-xs text-slate-400 mt-0.5">Searching…</p>
      )}

      {open && results.length > 0 && (
        <ul className="absolute z-10 left-0 right-0 mt-0.5 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded shadow-md">
          {results.map((w) => (
            <li key={w.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  // Use mousedown so it fires before the blur that closes the list
                  e.preventDefault();
                  handleSelect(w);
                }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-50 flex items-center gap-2"
              >
                <span className="font-arabic" dir="rtl">{w.text}</span>
                {w.transliteration && (
                  <span className="text-slate-400 text-xs">{w.transliteration}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedWord && (
        <p className="text-xs text-slate-400 mt-0.5">
          ID: {selectedWord.id}
          {selectedWord.transliteration && ` · ${selectedWord.transliteration}`}
        </p>
      )}
    </div>
  );
}

// ── Word header inline editor ──────────────────────────────────────────────

function WordHeaderEdit({ word, onSave, onMerged, onCancel }) {
  const [text, setText] = useState(word.text);
  const [transliteration, setTransliteration] = useState(word.transliteration || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // When the new text collides with an existing word, offer to merge into it.
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);

  // If the original text was "<base> <particle>", the dropped suffix is recorded
  // as a governing particle on the target when merging.
  const droppedParticle = (() => {
    const orig = (word.text || "").trim();
    const base = text.trim();
    if (base && orig.startsWith(base) && orig.length > base.length) {
      const rest = orig.slice(base.length).trim();
      if (rest && rest.length <= 4) return rest; // a single harf, not another word
    }
    return "";
  })();

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMergeTarget(null);
    try {
      const updated = await patchWord(word.id, { text, transliteration });
      onSave(updated);
    } catch (err) {
      setError(err.message);
      // On a unique-text collision, find the existing word so we can offer a merge.
      const collided = /already exists/i.test(err.message || "") || !!(err.data && err.data.text);
      if (collided) {
        try {
          const base = text.trim();
          const baseNorm = normForMatch(base);
          const results = await searchWords(base);
          // Only offer a merge with a word whose text matches what we typed
          // (ignoring harakat). searchWords does substring matching, so never
          // fall back to an arbitrary hit — that's how بيت matched البيتزا.
          const found = (results || []).find(
            (w) => w.id !== word.id && normForMatch(w.text) === baseNorm
          );
          if (found) setMergeTarget(found);
        } catch { /* ignore lookup failure */ }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeTarget) return;
    setMerging(true);
    setError(null);
    try {
      const target = await mergeWord(word.id, { target_id: mergeTarget.id, add_particle: droppedParticle });
      (onMerged || onSave)(target);
    } catch (err) {
      setError(err.message);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Arabic text</label>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          dir="rtl"
          lang="ar"
          className="w-full px-2 py-1 rounded border border-slate-300 text-lg font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Transliteration</label>
        <input
          type="text"
          value={transliteration}
          onChange={(e) => setTransliteration(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <EditActions onSave={handleSave} onCancel={onCancel} saving={saving} error={error} />

      {mergeTarget && (() => {
        const vocalisationDiffers = (mergeTarget.text || "").trim() !== text.trim();
        return (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 space-y-2">
            <p className="text-xs text-amber-800">
              A matching word already exists. Merging moves all uses onto it and
              deletes this one.
            </p>
            {/* Show the target's exact vocalisation so you don't merge into the wrong form. */}
            <div className="rounded-md bg-white border border-amber-200 px-3 py-2 text-center">
              <p className="text-[10px] uppercase tracking-wide text-amber-500 mb-0.5">Existing word</p>
              <p className="font-arabic text-2xl text-slate-800" dir="rtl">{mergeTarget.text}</p>
              {mergeTarget.transliteration && (
                <p className="text-xs text-slate-400 italic">{mergeTarget.transliteration}</p>
              )}
              <p className="text-[10px] text-slate-300 mt-0.5">id {mergeTarget.id}</p>
            </div>
            {vocalisationDiffers && (
              <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5">
                ⚠ Different vocalisation — you typed{" "}
                <span className="font-arabic" dir="rtl">{text.trim()}</span> but the existing word is{" "}
                <span className="font-arabic" dir="rtl">{mergeTarget.text}</span>. Merging keeps the
                existing spelling; cancel and rename instead if that's wrong.
              </p>
            )}
            {droppedParticle && (
              <p className="text-xs text-amber-800">
                The dropped <span className="font-arabic" dir="rtl">{droppedParticle}</span> will be recorded as a particle.
              </p>
            )}
            <button
              type="button"
              onClick={handleMerge}
              disabled={merging}
              className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
            >
              {merging ? "Merging…" : "Merge into the existing word"}
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// ── Sense inline editor ────────────────────────────────────────────────────

const VERB_FORM_OPTIONS = [
  "", "I-a", "I-i", "I-u", "II", "III", "IV", "V", "VI", "VII", "VIII", "X",
];

function SenseEdit({ sense, onSave, onCancel }) {
  const [definition, setDefinition] = useState(sense.definition || "");
  const [baseDefinition, setBaseDefinition] = useState(sense.base_definition || "");
  const [usageNotes, setUsageNotes] = useState(sense.usage_notes || "");
  const [verbForm, setVerbForm] = useState(sense.verb_form || "");

  // Verb form text values — these are what's actually displayed in the UI.
  // Displayed value = sense.past / sense.present / etc. (text fields).
  // The *_word_id FKs are secondary; clearing text also clears the FK.
  const [pastText, setPastText] = useState(sense.past || "");
  const [presentText, setPresentText] = useState(sense.present || "");
  const [imperativeText, setImperativeText] = useState(sense.imperative || "");
  const [masdarText, setMasdarText] = useState(sense.masdar || "");
  const [ismFaailText, setIsmFaailText] = useState(sense.ism_faail || "");
  const [ismMafoolText, setIsmMafoolText] = useState(sense.ism_mafool || "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        definition,
        base_definition: baseDefinition,
        usage_notes: usageNotes,
        verb_form: verbForm,
        past: pastText,
        present: presentText,
        imperative: imperativeText,
        masdar: masdarText,
        ism_faail: ismFaailText,
        ism_mafool: ismMafoolText,
        // Clear FKs when text is cleared so they don't override the display
        past_word_id: pastText ? (sense.past_word?.id ?? null) : null,
        present_word_id: presentText ? (sense.present_word?.id ?? null) : null,
        imperative_word_id: imperativeText ? (sense.imperative_word?.id ?? null) : null,
        masdar_word_id: masdarText ? (sense.masdar_word?.id ?? null) : null,
        ism_faail_word_id: ismFaailText ? (sense.ism_faail_word?.id ?? null) : null,
        ism_mafool_word_id: ismMafoolText ? (sense.ism_mafool_word?.id ?? null) : null,
      };
      const updated = await patchSense(sense.id, payload);
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Clearable Arabic text input for each verb form field
  const VerbField = ({ label, value, onChange }) => (
    <div>
      <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
      <div className="flex gap-1">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir="rtl"
          lang="ar"
          className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-2 py-1 rounded border border-slate-300 text-xs text-slate-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
            title="Clear"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Definition</label>
        <textarea
          value={definition}
          onChange={(e) => setDefinition(e.target.value)}
          rows={2}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">
          Base meaning <span className="text-slate-400">— shown in brackets, e.g. "to see"</span>
        </label>
        <input
          type="text"
          value={baseDefinition}
          onChange={(e) => setBaseDefinition(e.target.value)}
          placeholder="Leave empty if this word is the citation form"
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Usage notes</label>
        <input
          type="text"
          value={usageNotes}
          onChange={(e) => setUsageNotes(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Verb form</label>
        <select
          value={verbForm}
          onChange={(e) => setVerbForm(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          {VERB_FORM_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt || "\u2014 none \u2014"}
            </option>
          ))}
        </select>
      </div>
      <VerbField label="Past" value={pastText} onChange={setPastText} />
      <VerbField label="Present" value={presentText} onChange={setPresentText} />
      <VerbField label="Imperative" value={imperativeText} onChange={setImperativeText} />
      <VerbField label="Masdar" value={masdarText} onChange={setMasdarText} />
      <VerbField label="Active participle (اسم الفاعل)" value={ismFaailText} onChange={setIsmFaailText} />
      <VerbField label="Passive participle (اسم المفعول)" value={ismMafoolText} onChange={setIsmMafoolText} />
      <EditActions onSave={handleSave} onCancel={onCancel} saving={saving} error={error} />
    </div>
  );
}

// ── Verb-forms-only inline editor ──────────────────────────────────────────
// Editor for the 6 verb form types. Each type has a primary text field (the
// existing Sense.<type> column) plus an unbounded list of extras stored in
// SenseExtraForm rows. On Save, the primary fields are PATCHed in one call
// and the extras are reconciled via per-row create / patch / delete calls.

const VERB_FORM_FIELDS = [
  { key: "past",       label: "Past",                ar: "ماضٍ" },
  { key: "present",    label: "Present",             ar: "مضارع" },
  { key: "imperative", label: "Imperative",          ar: "أمر" },
  { key: "masdar",     label: "Masdar",              ar: "مصدر" },
  { key: "ism_faail",  label: "Active participle",   ar: "اسم الفاعل" },
  { key: "ism_mafool", label: "Passive participle",  ar: "اسم المفعول" },
];

function VerbFormsEdit({ sense, onSave, onCancel }) {
  // Primary text values (mirror Sense.<key>)
  const [primary, setPrimary] = useState(() => {
    const out = {};
    VERB_FORM_FIELDS.forEach(({ key }) => { out[key] = sense[key] || ""; });
    return out;
  });

  // Extras grouped by form_type. Existing rows carry an `id`; new rows have
  // id=null. Each row: { id, text, transliteration, _delete? }
  const [extras, setExtras] = useState(() => {
    const out = {};
    VERB_FORM_FIELDS.forEach(({ key }) => { out[key] = []; });
    (sense.extra_forms || []).forEach((ex) => {
      if (!out[ex.form_type]) out[ex.form_type] = [];
      out[ex.form_type].push({
        id: ex.id,
        text: ex.text || "",
        transliteration: ex.transliteration || "",
      });
    });
    return out;
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const updatePrimary = (key, value) =>
    setPrimary((p) => ({ ...p, [key]: value }));

  const updateExtra = (key, idx, patch) =>
    setExtras((prev) => ({
      ...prev,
      [key]: prev[key].map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));

  const addExtra = (key) =>
    setExtras((prev) => ({
      ...prev,
      [key]: [...prev[key], { id: null, text: "", transliteration: "" }],
    }));

  const removeExtra = (key, idx) =>
    setExtras((prev) => ({
      ...prev,
      [key]: prev[key]
        .map((row, i) => (i === idx ? { ...row, _delete: true } : row))
        // Drop rows that were never persisted
        .filter((row, i) => !(i === idx && row.id == null)),
    }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // 1. Build payload for primary fields
      const payload = {};
      VERB_FORM_FIELDS.forEach(({ key }) => {
        payload[key] = primary[key];
        const fkKey = `${key}_word_id`;
        const fkObj = sense[`${key}_word`];
        // Keep existing FK if text was kept, clear if text cleared
        payload[fkKey] = primary[key] ? (fkObj?.id ?? null) : null;
      });

      // 2. Patch the Sense
      let updated = await patchSense(sense.id, payload);

      // 3. Reconcile extras: deletes, updates, creates
      const ops = [];
      Object.entries(extras).forEach(([formType, rows]) => {
        rows.forEach((row) => {
          if (row._delete && row.id != null) {
            ops.push(deleteSenseExtraForm(row.id));
          } else if (row.id == null && row.text.trim()) {
            ops.push(
              addSenseExtraForm(sense.id, {
                form_type: formType,
                text: row.text.trim(),
                transliteration: row.transliteration.trim(),
              })
            );
          } else if (row.id != null && !row._delete) {
            // Find original to detect diff
            const orig = (sense.extra_forms || []).find((e) => e.id === row.id);
            if (
              !orig ||
              orig.text !== row.text ||
              (orig.transliteration || "") !== row.transliteration
            ) {
              ops.push(
                patchSenseExtraForm(row.id, {
                  text: row.text.trim(),
                  transliteration: row.transliteration.trim(),
                })
              );
            }
          }
        });
      });
      if (ops.length) await Promise.all(ops);

      // 4. Re-fetch sense for fresh extras list. Cheap trick: PATCH no-op so
      // the response includes the up-to-date extra_forms array.
      updated = await patchSense(sense.id, {});

      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
        Edit verb forms
      </p>
      {VERB_FORM_FIELDS.map(({ key, label, ar }) => {
        const visibleExtras = extras[key].filter((r) => !r._delete);
        return (
          <div key={key} className="space-y-1.5">
            <label className="block text-xs text-slate-500">
              {label} <span className="text-slate-400">({ar})</span>
            </label>
            <div className="flex gap-1">
              <input
                type="text"
                value={primary[key]}
                onChange={(e) => updatePrimary(key, e.target.value)}
                dir="rtl"
                lang="ar"
                placeholder="—"
                className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {primary[key] && (
                <button
                  type="button"
                  onClick={() => updatePrimary(key, "")}
                  className="px-2 py-1 rounded border border-slate-300 text-xs text-slate-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                  title="Clear primary"
                >✕</button>
              )}
            </div>
            {visibleExtras.map((row, idx) => {
              const realIdx = extras[key].indexOf(row);
              return (
                <div key={row.id ?? `new-${realIdx}`} className="flex gap-1 pl-3 border-l-2 border-indigo-200">
                  <input
                    type="text"
                    value={row.text}
                    onChange={(e) => updateExtra(key, realIdx, { text: e.target.value })}
                    dir="rtl"
                    lang="ar"
                    placeholder="Arabic"
                    className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <input
                    type="text"
                    value={row.transliteration}
                    onChange={(e) => updateExtra(key, realIdx, { transliteration: e.target.value })}
                    placeholder="translit."
                    className="w-24 px-2 py-1 rounded border border-slate-300 text-xs italic focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeExtra(key, realIdx)}
                    className="px-2 py-1 rounded border border-slate-300 text-xs text-slate-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                    title="Remove this alternate"
                  >✕</button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => addExtra(key)}
              className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
            >
              + Add another {label.toLowerCase()}
            </button>
          </div>
        );
      })}
      <EditActions onSave={handleSave} onCancel={onCancel} saving={saving} error={error} />
    </div>
  );
}

// ── Plural inline editor ───────────────────────────────────────────────────

function PluralEdit({ plural, onSave, onCancel }) {
  const [form, setForm] = useState(plural.form || "");
  const [transliteration, setTransliteration] = useState(plural.transliteration || "");
  const [notes, setNotes] = useState(plural.notes || "");
  const [pluralWordId, setPluralWordId] = useState(plural.plural_word?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await patchPlural(plural.id, { form, transliteration, notes, plural_word_id: pluralWordId });
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Form (Arabic)</label>
        <input
          type="text"
          value={form}
          onChange={(e) => setForm(e.target.value)}
          dir="rtl"
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Transliteration</label>
        <input
          type="text"
          value={transliteration}
          onChange={(e) => setTransliteration(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Notes</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <WordSelector label="Link to word (optional)" initialWord={plural.plural_word} onSelect={setPluralWordId} />
      <EditActions onSave={handleSave} onCancel={onCancel} saving={saving} error={error} />
    </div>
  );
}

// ── Add sense (manual definition) form ────────────────────────────────────

function AddSenseForm({ wordId, onAdded, onCancel }) {
  const [definition, setDefinition] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const def = definition.trim();
    if (!def) return;
    setSaving(true);
    setError(null);
    try {
      const newSense = await createSense(wordId, def);
      onAdded(newSense);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-4 py-3 space-y-2 border-t border-slate-100 bg-slate-50">
      <label className="block text-xs text-slate-500">Definition (English)</label>
      <input
        type="text"
        value={definition}
        onChange={(e) => setDefinition(e.target.value)}
        placeholder="e.g. house, home"
        autoFocus
        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!definition.trim() || saving}
          className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save definition"}
        </button>
      </div>
    </form>
  );
}

// ── Add plural form ────────────────────────────────────────────────────────

function AddPluralForm({ wordId, onAdded, onCancel }) {
  const [form, setForm] = useState("");
  const [transliteration, setTransliteration] = useState("");
  const [notes, setNotes] = useState("");
  const [pluralWordId, setPluralWordId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!form.trim()) {
      setError("Form (Arabic) is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await addPlural(wordId, { form, transliteration, notes, plural_word_id: pluralWordId });
      onAdded(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 p-2 border border-emerald-200 rounded-lg bg-emerald-50 space-y-2">
      <p className="text-xs font-medium text-emerald-700">Add plural</p>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Form (Arabic)</label>
        <input
          type="text"
          value={form}
          onChange={(e) => setForm(e.target.value)}
          dir="rtl"
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Transliteration</label>
        <input
          type="text"
          value={transliteration}
          onChange={(e) => setTransliteration(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-0.5">Notes</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-2 py-1 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <WordSelector label="Link to existing word (optional)" initialWord={null} onSelect={setPluralWordId} />
      <EditActions onSave={handleSave} onCancel={onCancel} saving={saving} error={error} />
    </div>
  );
}

/** A sense's definition, followed by the citation-form meaning in brackets for
 *  an inflected word — "we will see (to see)".
 *
 *  Shared because the definition renders twice: as a clickable edit target for
 *  admins and as plain text for everyone else. Inlining the brackets in one
 *  branch only is exactly how they went missing for admins.
 */
function SenseDefinitionText({ sense }) {
  return (
    <>
      {sense.definition}
      {sense.base_definition && (
        <span className="text-dt-parchment-dim"> ({sense.base_definition})</span>
      )}
    </>
  );
}

// ── Fetch-definition modals ────────────────────────────────────────────────

// Word type an admin can declare before an AI look-up. Undiacritized Arabic is
// ambiguous (تَذَاكِر "tickets" vs. تُذَاكِر "you study"), so letting the admin
// state the reading up front stops the model importing a verb paradigm onto a noun.
const WORD_TYPE_HINTS = [
  { value: "", label: "Auto" },
  { value: "vocab", label: "Noun" },
  { value: "verb", label: "Verb" },
  { value: "expression", label: "Expr" },
];

/** Step 1: admin edits the search word before any API call is made.
 *  Also shows local DB word matches so the admin can spot existing entries. */
function FetchSearchModal({ defaultWord, force, source, onSearch, onCancel, searching }) {
  const [searchWord, setSearchWord] = useState(defaultWord);
  const [wordTypeHint, setWordTypeHint] = useState("");
  const [localResults, setLocalResults] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const debounceRef = useRef(null);

  // Search local DB as admin types
  useEffect(() => {
    const q = searchWord.trim();
    if (!q) { setLocalResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLocalLoading(true);
      try {
        const results = await searchWords(q);
        setLocalResults((results || []).slice(0, 8));
      } catch { setLocalResults([]); }
      finally { setLocalLoading(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchWord]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchWord.trim()) onSearch(searchWord.trim(), wordTypeHint);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="dark-scope surface rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-dt-line">
          <h2 className="font-semibold text-slate-800 text-base">
            {force ? "Re-fetch definition" : "Fetch definition"}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Edit the search word if needed — try a base form or remove prefixes.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-5 pt-4 pb-3 space-y-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Search word</label>
            <input
              type="text"
              value={searchWord}
              onChange={(e) => setSearchWord(e.target.value)}
              dir="rtl"
              lang="ar"
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-base font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Word type — AI only; the dictionary API derives its own POS. */}
          {source === "ai" && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">Word type</label>
              <div className="flex gap-1.5">
                {WORD_TYPE_HINTS.map((opt) => (
                  <button
                    key={opt.value || "auto"}
                    type="button"
                    onClick={() => setWordTypeHint(opt.value)}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                      wordTypeHint === opt.value
                        ? "border-emerald-500 bg-emerald-600 text-white"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                {wordTypeHint
                  ? "The AI must use this reading — no verb forms are saved for a noun."
                  : "The AI decides. Pick a type if it has guessed wrong before."}
              </p>
            </div>
          )}

          {/* Local DB suggestions */}
          {(localLoading || localResults.length > 0) && (
            <div>
              <p className="text-xs text-slate-400 mb-1.5">
                {localLoading ? "Searching local words…" : "Already in local database:"}
              </p>
              {!localLoading && (
                <ul className="space-y-1 max-h-48 overflow-y-auto">
                  {localResults.map((w) => (
                    <li key={w.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-slate-100 bg-slate-50">
                      <span className="font-arabic text-slate-800 text-sm flex-1" dir="rtl">{w.text}</span>
                      {w.senses?.[0]?.definition && (
                        <span className="text-xs text-slate-400 truncate max-w-[120px]">{w.senses[0].definition}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setSearchWord(w.text)}
                        className="text-xs px-2 py-0.5 rounded border border-slate-300 text-slate-500 hover:bg-white shrink-0"
                        title="Use as search word"
                      >
                        Use
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={searching}
              className="px-4 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!searchWord.trim() || searching}
              className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {searching && (
                <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {searching ? "Searching…" : "Search API →"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const FORM_LABELS = {
  past: "Past (ماضٍ)",
  present: "Present (مضارع)",
  imperative: "Imperative (أمر)",
  masdar: "Masdar (مصدر)",
  plural: "Plural (جمع)",
  singular: "Singular (مفرد)",
  ism_faail: "Active participle (اسم الفاعل)",
  ism_mafool: "Passive participle (اسم المفعول)",
};

function FetchConfirmModal({ preview, onConfirm, onCancel, saving }) {
  const forms = preview.forms || {};
  const hasAnyForm = Object.keys(forms).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="dark-scope surface rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-dt-line">
          <h2 className="font-semibold text-slate-800 text-base">Confirm import</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Review the matched entry before saving to the database.
          </p>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3 max-h-96 overflow-y-auto">
          {/* Search word used */}
          {preview.search_word && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-28 shrink-0">Searched for</span>
              <span className="font-arabic text-slate-600" dir="rtl">{preview.search_word}</span>
            </div>
          )}

          {/* Matched API word */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 w-28 shrink-0">API matched word</span>
            <span className="font-arabic text-slate-900 font-medium" dir="rtl">
              {preview.matched_word || "—"}
            </span>
          </div>

          {/* Part of speech */}
          {preview.part_of_speech && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-28 shrink-0">Part of speech</span>
              <span className="text-sm text-slate-700 capitalize">{preview.part_of_speech}</span>
            </div>
          )}

          {/* Number of the searched word — it decides which way the noun link
              is written (a plural gets its singular, a singular its plural). */}
          {preview.number && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-28 shrink-0">Number</span>
              <span className="text-sm text-slate-700 capitalize">
                {preview.number}
                <span className="text-xs text-slate-400">
                  {preview.number === "plural" ? " — its singular is linked" : " — its plural is linked"}
                </span>
              </span>
            </div>
          )}

          {/* Saved type — shown when the admin declared one, so it's visible
              that the AI's own guess was overridden before anything is written. */}
          {preview.word_type_hint && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-28 shrink-0">Saved as</span>
              <span className="text-sm text-slate-700">
                {(WORD_TYPE_HINTS.find((o) => o.value === preview.word_type_hint) || {}).label || preview.word_type_hint}
                <span className="text-xs text-slate-400"> (you chose this)</span>
              </span>
            </div>
          )}

          {/* Definition */}
          <div className="flex gap-2">
            <span className="text-xs text-slate-500 w-28 shrink-0 pt-0.5">Definition</span>
            <span className="text-sm text-slate-800">
              {preview.definition || <em className="text-slate-400">none</em>}
              {preview.base_definition && (
                <span className="text-slate-400"> ({preview.base_definition})</span>
              )}
            </span>
          </div>

          {/* Verb forms / plural */}
          {hasAnyForm && (
            <div className="border-t border-slate-100 pt-3 space-y-1.5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Forms to be saved</p>
              {Object.entries(forms).map(([key, value]) => (
                value ? (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-28 shrink-0">
                      {FORM_LABELS[key] || key}
                    </span>
                    <span className="font-arabic text-slate-900" dir="rtl">{value}</span>
                  </div>
                ) : null
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && (
              <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {saving ? "Saving…" : "Confirm & save"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ── Main WordDetail component ──────────────────────────────────────────────

// ── Verb governing particles (حرف جر) — display + admin add/edit/remove ──────
const EMPTY_PARTICLE_FORM = { particle: "", meaning: "", example: "", example_translation: "" };

function ParticlesSection({ sense, isAdmin }) {
  const [items, setItems] = useState(sense.particles || []);
  // mode: null | "add" | <id being edited>
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState(EMPTY_PARTICLE_FORM);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { setItems(sense.particles || []); }, [sense.particles]);

  const openAdd = () => { setMode("add"); setForm(EMPTY_PARTICLE_FORM); setErr(null); };
  const openEdit = (p) => {
    setMode(p.id);
    setForm({ particle: p.particle || "", meaning: p.meaning || "", example: p.example || "", example_translation: p.example_translation || "" });
    setErr(null);
  };
  const cancel = () => { setMode(null); setErr(null); };

  const save = async () => {
    if (!form.particle.trim() || busy) return;
    setBusy(true); setErr(null);
    const payload = {
      particle: form.particle.trim(), meaning: form.meaning.trim(),
      example: form.example.trim(), example_translation: form.example_translation.trim(),
    };
    try {
      if (mode === "add") {
        const created = await addSenseParticle(sense.id, payload);
        setItems((prev) => [...prev, created]);
      } else {
        const updated = await patchSenseParticle(mode, payload);
        setItems((prev) => prev.map((x) => (x.id === mode ? updated : x)));
      }
      setMode(null);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await deleteSenseParticle(id); setItems((prev) => prev.filter((x) => x.id !== id)); }
    catch { /* ignore */ }
  };

  if (!isAdmin && items.length === 0) return null;
  const inputCls = "w-full px-2 py-1.5 rounded border border-slate-200 text-sm";
  const editor = (
    <div className="rounded-lg border border-slate-200 p-2 space-y-1.5">
      <input value={form.particle} dir="rtl" onChange={(e) => setForm((f) => ({ ...f, particle: e.target.value }))} placeholder="حرف (e.g. عَنْ)" className={inputCls} />
      <input value={form.meaning} onChange={(e) => setForm((f) => ({ ...f, meaning: e.target.value }))} placeholder="Meaning with this particle" className={inputCls} />
      <input value={form.example} dir="rtl" onChange={(e) => setForm((f) => ({ ...f, example: e.target.value }))} placeholder="Example (optional)" className={inputCls} />
      <input value={form.example_translation} onChange={(e) => setForm((f) => ({ ...f, example_translation: e.target.value }))} placeholder="Example translation (optional)" className={inputCls} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="px-3 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        <button onClick={cancel} className="px-3 py-1 rounded-lg border border-slate-300 text-slate-600 text-xs">Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Used with (حرف جرّ)</p>
      <ul className="space-y-1">
        {items.map((p) => (
          <li key={p.id} className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
            {mode === p.id ? editor : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm text-slate-700">
                    <span className="font-arabic text-base font-semibold" dir="rtl">{p.particle}</span>
                    {p.meaning && <span className="text-slate-500"> — {p.meaning}</span>}
                  </p>
                  {p.example && <p className="text-sm font-arabic text-slate-600 mt-0.5" dir="rtl">{p.example}</p>}
                  {p.example_translation && <p className="text-xs text-slate-400">{p.example_translation}</p>}
                </div>
                {isAdmin && (
                  <div className="flex items-center shrink-0">
                    <PencilButton onClick={() => openEdit(p)} />
                    <TrashButton onClick={() => remove(p.id)} />
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {isAdmin && mode === "add" && editor}
      {isAdmin && mode !== "add" && (
        <button onClick={openAdd} className="text-xs px-2.5 py-1 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50">+ Add particle</button>
      )}
    </div>
  );
}


export default function WordDetail({
  word,
  // Set when opened by clicking a word in a scenario sentence:
  // { surfaceText, morphology, sentence, turnId, tokenIdx }. The surface form (and its
  // per-occurrence structure, #92) can differ from Word.text in case ending.
  occurrence,
  loading,
  error,
  onClear,
  topicsForWord,
  topicsForWordLoading,
  topicsForWordError,
  onTopicSelect,
  onFormWordClick,
  isAdmin,
  onWordUpdate,
  currentTopicId,
  currentWordType,
  onTopicWordTypeChange,
  topicOwnedByUser = false,
}) {
  const [editingHeader, setEditingHeader] = useState(false);
  const [editingSenseId, setEditingSenseId] = useState(null);
  const [editingVerbFormsSenseId, setEditingVerbFormsSenseId] = useState(null);
  const [editingPluralId, setEditingPluralId] = useState(null);
  const [addingPlural, setAddingPlural] = useState(false);
  const [addingSingular, setAddingSingular] = useState(false);
  const [singularSearchQ, setSingularSearchQ] = useState("");
  const [singularSearchResults, setSingularSearchResults] = useState([]);
  const [singularSearchLoading, setSingularSearchLoading] = useState(false);
  const [localSingularOf, setLocalSingularOf] = useState(null);

  // Fetch-definition state (three-step: search → preview → confirm)
  const [fetchSearchModal, setFetchSearchModal] = useState(null); // { force, source }
  const [fetchingDef, setFetchingDef] = useState(false);
  const [fetchingSource, setFetchingSource] = useState(null); // "dict" | "ai" | null
  const [fetchDefError, setFetchDefError] = useState(null);
  const [fetchDefSuccess, setFetchDefSuccess] = useState(null);
  const [aiFetchModel, setAiFetchModel] = useState(() => {
    try { return localStorage.getItem("aiFetchModel") || ""; } catch { return ""; }
  });
  const [fetchPreview, setFetchPreview] = useState(null);   // { preview data, force, searchWord }
  const [savingDef, setSavingDef] = useState(false);
  const [userFetchingDef, setUserFetchingDef] = useState(false);

  // Paradigm sheet state: { senseId, wordText } | null
  const [paradigmTarget, setParadigmTarget] = useState(null);

  // For a conjugated form, the fetched root verb so its full family of forms
  // can be shown inline under the banner (no click-through needed).
  const [rootFamily, setRootFamily] = useState(null);
  const [unlinkingConj, setUnlinkingConj] = useState(false);

  // Local copies of mutable data so edits are reflected immediately
  const [localWord, setLocalWord] = useState(null);
  const [localSenses, setLocalSenses] = useState(null);
  const [localPlurals, setLocalPlurals] = useState(null);
  const [showStructure, setShowStructure] = useState(false);
  const [addingSense, setAddingSense] = useState(false);

  // Sync local state when the word prop changes
  useEffect(() => {
    setLocalWord(word || null);
    setLocalSenses(word?.senses || null);
    setLocalPlurals(word?.plurals || null);
    setLocalSingularOf(word?.singular_of || null);
    setEditingHeader(false);
    setEditingSenseId(null);
    setEditingVerbFormsSenseId(null);
    setEditingPluralId(null);
    setAddingPlural(false);
    setAddingSingular(false);
    setSingularSearchQ("");
    setSingularSearchResults([]);
    setAddingSense(false);
    setFetchDefError(null);
    setFetchDefSuccess(null);
    setParadigmTarget(null);
  }, [word]);

  // For a conjugated form, fetch the root verb so its whole family of forms can
  // be shown inline under the banner (no click-through).
  const conjRootId = word?.conjugation_info?.word_id || null;
  useEffect(() => {
    if (!conjRootId) {
      setRootFamily(null);
      return;
    }
    let cancelled = false;
    fetchWordDetail(conjRootId)
      .then((data) => { if (!cancelled) setRootFamily(data); })
      .catch(() => { if (!cancelled) setRootFamily(null); });
    return () => { cancelled = true; };
  }, [conjRootId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-6 h-6 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75v1.5M17.127 6.873l-1.06 1.06M19.25 12h-1.5M17.127 17.127l-1.06-1.06M12 19.25v-1.5M6.873 17.127l1.06-1.06M4.75 12h1.5M6.873 6.873l1.06 1.06" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!localWord) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
        <p className="text-sm text-slate-400">Tap a word to see details</p>
      </div>
    );
  }

  const displayWord = localWord;
  // When opened from a sentence, show that occurrence's structure — never the
  // global Word.morphology, whose case may not match this occurrence. The
  // global blob remains the source for non-sentence entry points (search).
  const structureData = occurrence ? occurrence.morphology : displayWord.morphology;
  const displaySenses = localSenses || [];
  const displayPlurals = localPlurals || [];
  const displaySingularOf = localSingularOf ?? (displayWord.singular_of || []);
  const isPlural = displaySingularOf.length > 0;
  const conjugationInfo = displayWord.conjugation_info || null;
  // If this word is a conjugated form of a root verb, treat it as "verb" regardless of
  // the per-topic word_type (which may still say "vocab" on topics where it wasn't promoted).
  const effectiveWordType = conjugationInfo ? "verb" : currentWordType;

  // A conjugated form inherits its root verb's definition (shown in the banner
  // and reused in the definition section) — so it isn't really "undefined".
  const conjDefinition = (conjugationInfo?.definition || "").trim();

  const isMissingDefinition =
    displaySenses.length === 0 ||
    displaySenses.every((s) => !s.definition || !s.definition.trim());

  // Admin veto on the automatic "form of <verb>" match. The match runs on
  // ḥarakāt-stripped text, so homographs collide (the noun تَذَاكِر "tickets"
  // normalizes the same as the verb form تُذَاكِر "you study"). The underlying
  // VerbConjugation row is a genuine form of the root verb, so it stays — only
  // this word stops being read as one of its forms.
  const handleUnlinkConjugation = async () => {
    if (!window.confirm(
      `Remove this conjugation link? "${displayWord.text}" will no longer be treated as a form of ${conjugationInfo.word_text}.`
    )) return;
    setUnlinkingConj(true);
    try {
      const updated = await setWordConjugationLink(displayWord.id, false);
      setLocalWord(updated);
      setLocalSenses(updated.senses || []);
      if (onWordUpdate) onWordUpdate(updated);
    } catch (err) {
      alert("Failed to unlink: " + err.message);
    } finally {
      setUnlinkingConj(false);
    }
  };

  const handleRestoreConjugation = async () => {
    setUnlinkingConj(true);
    try {
      const updated = await setWordConjugationLink(displayWord.id, true);
      setLocalWord(updated);
      setLocalSenses(updated.senses || []);
      if (onWordUpdate) onWordUpdate(updated);
    } catch (err) {
      alert("Failed to restore: " + err.message);
    } finally {
      setUnlinkingConj(false);
    }
  };

  // Step 1: open search modal (no API call yet)
  const handleFetchDefinition = (force = false, source = "dict") => {
    setFetchDefError(null);
    setFetchDefSuccess(null);
    setFetchSearchModal({ force, source });
  };

  // Learner self-service: one-tap AI definition for an undefined word in their
  // own topic (backend allows it only for owned, undefined words, once).
  const handleUserFetchDefinition = async () => {
    setUserFetchingDef(true);
    setFetchDefError(null);
    setFetchDefSuccess(null);
    try {
      const data = await fetchDefinitionFromAI(displayWord.id, { topicId: currentTopicId });
      if (data.word) {
        setLocalWord(data.word);
        setLocalSenses(data.word.senses || null);
        setLocalPlurals(data.word.plurals || null);
        if (onWordUpdate) onWordUpdate(data.word);
      }
      setFetchDefSuccess("Definition added.");
    } catch (e) {
      setFetchDefError(e.message || "Could not fetch the definition.");
    } finally {
      setUserFetchingDef(false);
    }
  };

  // Step 2: admin submitted search word → preview API
  const handleSearch = async (searchWord, wordTypeHint = "") => {
    const force = fetchSearchModal?.force ?? false;
    const source = fetchSearchModal?.source ?? "dict";
    setFetchingDef(true);
    setFetchingSource(source);
    setFetchDefError(null);
    try {
      const data =
        source === "ai"
          ? await fetchDefinitionFromAI(displayWord.id, {
              force,
              preview: true,
              searchWord,
              model: aiFetchModel,
              topicId: currentTopicId,
              wordTypeHint,
              contextSentence: occurrence?.sentence || "",
            })
          : await fetchDefinitionFromDictionary(displayWord.id, force, true, searchWord);
      setFetchPreview({ ...data, force, searchWord, source, wordTypeHint });
      setFetchSearchModal(null);
    } catch (err) {
      setFetchDefError(err.message || "Failed to fetch definition.");
      setFetchSearchModal(null);
    } finally {
      setFetchingDef(false);
      setFetchingSource(null);
    }
  };

  // Step 3: admin confirmed preview → save
  const handleConfirmFetch = async () => {
    if (!fetchPreview) return;
    setSavingDef(true);
    setFetchDefError(null);
    try {
      const source = fetchPreview.source ?? "dict";
      const data =
        source === "ai"
          ? await fetchDefinitionFromAI(displayWord.id, {
              force: fetchPreview.force,
              preview: false,
              searchWord: fetchPreview.searchWord,
              model: aiFetchModel,
              topicId: currentTopicId,
              wordTypeHint: fetchPreview.wordTypeHint || "",
              contextSentence: occurrence?.sentence || "",
            })
          : await fetchDefinitionFromDictionary(
              displayWord.id,
              fetchPreview.force,
              false,
              fetchPreview.searchWord,
            );
      setLocalWord(data.word);
      setLocalSenses(data.word.senses || []);
      setLocalPlurals(data.word.plurals || []);
      if (onWordUpdate) onWordUpdate(data.word);
      const ex = data.debug_extracted || {};
      const formSummary = ["past", "present", "imperative", "masdar", "ism_faail", "ism_mafool"]
        .filter((k) => ex[k])
        .map((k) => `${k}: ${ex[k]}`)
        .join(" · ");
      const imp = data.imported || {};
      const extras = [];
      if (imp.word_type_set) extras.push(`type → ${imp.word_type_set}`);
      if (imp.conjugations_stored) extras.push(`${imp.conjugations_stored} conjugations`);
      const extraStr = extras.length ? ` [${extras.join(" · ")}]` : "";
      // If the AI changed TopicWord.word_type, notify the parent so its
      // word lists + the "Type in topic" toggle pick up the new value.
      if (imp.word_type_set && onTopicWordTypeChange) {
        onTopicWordTypeChange(displayWord.id, imp.word_type_set, {});
      }
      setFetchDefSuccess(
        (data.message || "Definition imported successfully.") +
          (formSummary ? ` (${formSummary})` : "") +
          extraStr
      );
      setFetchPreview(null);
    } catch (err) {
      setFetchDefError(err.message || "Failed to save definition.");
      setFetchPreview(null);
    } finally {
      setSavingDef(false);
    }
  };

  const handleWordSaved = (updatedWord) => {
    setLocalWord(updatedWord);
    setEditingHeader(false);
    if (onWordUpdate) onWordUpdate(updatedWord);
  };

  // After a merge the displayed word becomes the target — refresh all of it.
  const handleWordMerged = (target) => {
    setLocalWord(target);
    setLocalSenses(target.senses || null);
    setLocalPlurals(target.plurals || null);
    setLocalSingularOf(target.singular_of || null);
    setEditingHeader(false);
    if (onWordUpdate) onWordUpdate(target);
  };

  const handleSenseSaved = (updatedSense) => {
    setLocalSenses((prev) =>
      (prev || []).map((s) => (s.id === updatedSense.id ? updatedSense : s))
    );
    setEditingSenseId(null);
    setEditingVerbFormsSenseId(null);
  };

  const handleSenseDeleted = async (senseId) => {
    if (!window.confirm("Delete this definition?")) return;
    try {
      await deleteSense(senseId);
      setLocalSenses((prev) => (prev || []).filter((s) => s.id !== senseId));
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSetPrimary = async (senseId) => {
    try {
      await setPrimarySense(senseId);
      setLocalSenses((prev) =>
        (prev || []).map((s) => ({ ...s, is_primary: s.id === senseId }))
      );
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSetDisplayForm = async (senseId, formKey) => {
    // Toggle: clicking the active form clears it (back to default = past).
    const current = (localSenses || []).find((s) => s.id === senseId);
    const next = current?.display_form === formKey ? "" : formKey;
    try {
      const updated = await patchSense(senseId, {
        display_form: next,
        // Setting a primary picker clears any extra-form picker on this sense
        display_extra_form_id: next ? null : current?.display_extra_form_id ?? null,
      });
      setLocalSenses((prev) =>
        (prev || []).map((s) =>
          s.id === senseId
            ? {
                ...s,
                display_form: updated.display_form ?? next,
                display_extra_form_id: next ? null : s.display_extra_form_id,
              }
            : next
            ? { ...s, display_form: "", display_extra_form_id: null }
            : s
        )
      );
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSetDisplayExtraForm = async (senseId, extraId) => {
    const current = (localSenses || []).find((s) => s.id === senseId);
    const next = current?.display_extra_form_id === extraId ? null : extraId;
    try {
      const updated = await patchSense(senseId, {
        display_extra_form_id: next,
        // Setting an extra picker clears the primary picker on this sense
        display_form: next ? "" : current?.display_form ?? "",
      });
      setLocalSenses((prev) =>
        (prev || []).map((s) =>
          s.id === senseId
            ? {
                ...s,
                display_extra_form_id: updated.display_extra_form_id ?? next,
                display_form: next ? "" : s.display_form,
              }
            : next
            ? { ...s, display_form: "", display_extra_form_id: null }
            : s
        )
      );
    } catch (e) {
      alert(e.message);
    }
  };

  const handleAdoptConjugationDefinition = async () => {
    if (!conjugationInfo?.definition) return;
    try {
      // If an own sense with matching text already exists, just promote it — don't duplicate
      const existing = (localSenses || []).find(
        (s) => s.definition === conjugationInfo.definition
      );
      if (existing) {
        await setPrimarySense(existing.id);
        setLocalSenses((prev) =>
          (prev || []).map((s) => ({ ...s, is_primary: s.id === existing.id }))
        );
      } else {
        const newSense = await createSense(displayWord.id, conjugationInfo.definition);
        await setPrimarySense(newSense.id);
        setLocalSenses((prev) => [
          ...(prev || []).map((s) => ({ ...s, is_primary: false })),
          { ...newSense, is_primary: true },
        ]);
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handlePluralSaved = (updatedPlural) => {
    setLocalPlurals((prev) =>
      (prev || []).map((p) => (p.id === updatedPlural.id ? updatedPlural : p))
    );
    setEditingPluralId(null);
  };

  const handlePluralAdded = (newPlural) => {
    setLocalPlurals((prev) => [...(prev || []), newPlural]);
    setAddingPlural(false);
  };

  const handlePluralDeleted = (pluralId) => {
    setLocalPlurals((prev) => (prev || []).filter((p) => p.id !== pluralId));
    if (editingPluralId === pluralId) setEditingPluralId(null);
  };

  const openParadigm = (senseId, wordText) => {
    setParadigmTarget({ senseId, wordText });
  };

  return (
    <>
      {/* Step 1: search word modal */}
      {fetchSearchModal && (
        <FetchSearchModal
          defaultWord={displayWord.text}
          force={fetchSearchModal.force}
          source={fetchSearchModal.source}
          onSearch={handleSearch}
          onCancel={() => setFetchSearchModal(null)}
          searching={fetchingDef}
        />
      )}

      {/* Step 2: fetch-definition confirmation modal */}
      {fetchPreview && (
        <FetchConfirmModal
          preview={fetchPreview}
          onConfirm={handleConfirmFetch}
          onCancel={() => setFetchPreview(null)}
          saving={savingDef}
        />
      )}

      {/* Word-structure blow-up modal */}
      {showStructure && structureData && (
        <WordMorphology word={structureData} onClose={() => setShowStructure(false)} />
      )}

      {/* Paradigm sheet overlay */}
      {paradigmTarget && (
        <ParadigmSheet
          senseId={paradigmTarget.senseId}
          wordText={paradigmTarget.wordText}
          isAdmin={isAdmin}
          onClose={() => setParadigmTarget(null)}
        />
      )}

      <div className="space-y-4 pb-6">
        {/* ── Header card ── */}
        <div className="surface-glass rounded-2xl px-4 pt-4 pb-3 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {editingHeader ? (
                <WordHeaderEdit
                  word={displayWord}
                  onSave={handleWordSaved}
                  onMerged={handleWordMerged}
                  onCancel={() => setEditingHeader(false)}
                />
              ) : (
                <div className="flex items-center gap-1 flex-wrap">
                  <h2 className="font-arabic text-3xl font-bold text-dt-gold-bright leading-tight" dir="rtl">
                    {occurrence?.surfaceText || displayWord.text}
                  </h2>
                  {isAdmin && <PencilButton onClick={() => setEditingHeader(true)} />}
                </div>
              )}
            </div>
            {onClear && (
              <button
                onClick={onClear}
                className="shrink-0 p-2 rounded-full text-dt-parchment-dim hover:text-dt-parchment hover:bg-dt-forest-soft"
                aria-label="Close word detail"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {displayWord.transliteration && (
            <p className="text-sm text-dt-parchment-dim italic">{displayWord.transliteration}</p>
          )}

          {/* ── Word type toggle (admin, when word is in current topic) ── */}
          {isAdmin && currentTopicId && word && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-dt-parchment-dim">Type in topic:</span>
              <div className="flex gap-1">
                {[
                  { type: "verb", label: "Verb", active: "bg-dt-gold text-dt-ink border-dt-gold font-semibold", inactive: "text-dt-parchment-dim border-dt-line hover:border-dt-gold" },
                  { type: "vocab", label: "Vocab", active: "bg-dt-gold text-dt-ink border-dt-gold font-semibold", inactive: "text-dt-parchment-dim border-dt-line hover:border-dt-gold" },
                  { type: "expression", label: "Expr", active: "bg-dt-gold text-dt-ink border-dt-gold font-semibold", inactive: "text-dt-parchment-dim border-dt-line hover:border-dt-gold" },
                ].map(({ type, label, active, inactive }) => (
                  <button
                    key={type}
                    disabled={effectiveWordType === type}
                    onClick={async () => {
                      try {
                        const result = await patchTopicWord(currentTopicId, word.id, { word_type: type });
                        if (type === "verb" && result.root_added) {
                          // Root auto-added; notify parent to refresh
                        } else if (type === "verb" && result.needs_root) {
                          const rootText = window.prompt(
                            `Root verb not found for "${word.text}".\n\nEnter the dictionary form (3rd person masc. past — ماضٍ):\n(Suggested: ${result.suggested_root || ""})`,
                            result.suggested_root || ""
                          );
                          if (rootText && rootText.trim()) {
                            try {
                              await setWordRoot(currentTopicId, word.id, rootText.trim());
                            } catch (e) {
                              alert(e.message);
                            }
                          }
                        }
                        if (onTopicWordTypeChange) onTopicWordTypeChange(word.id, type, result);
                      } catch (err) {
                        alert("Failed to change type: " + err.message);
                      }
                    }}
                    className={`px-2 py-0.5 text-xs font-medium rounded border transition-colors ${effectiveWordType === type ? active + " cursor-default" : inactive}`}
                  >{label}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Word structure (morphology blow-up) trigger ── */}
        {structureData && (
          <button
            type="button"
            onClick={() => setShowStructure(true)}
            className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-bold text-sm transition-[filter] hover:brightness-105"
            style={{ background: "linear-gradient(90deg, var(--gold), var(--gold-bright))", color: "var(--ink)" }}
          >
            ◇ View Word Structure
          </button>
        )}

        {/* ── Conjugated-form banner ── */}
        {conjugationInfo && (
          <div className="rounded-2xl px-4 py-3 space-y-1" style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5" style={{ color: "var(--gold-bright)" }}>
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-semibold">Conjugated form</p>
              </div>
              {isAdmin && (
                <button
                  type="button"
                  disabled={unlinkingConj}
                  onClick={handleUnlinkConjugation}
                  className="px-2.5 py-1 text-xs rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 shrink-0"
                  title={`Not a form of ${conjugationInfo.word_text} — remove this link`}
                >
                  {unlinkingConj ? "Unlinking…" : "Unlink"}
                </button>
              )}
            </div>
            <p className="text-sm" style={{ color: "var(--parchment)" }}>
              {tagsToLabel(conjugationInfo.tags)} of{" "}
              <button
                type="button"
                className="font-arabic font-bold underline underline-offset-2"
                style={{ color: "var(--gold-bright)" }}
                onClick={() => onFormWordClick && onFormWordClick(conjugationInfo.word_id, conjugationInfo.word_text)}
                dir="rtl"
              >
                {conjugationInfo.word_text}
              </button>
            </p>
            <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>{conjugationInfo.definition}</p>
          </div>
        )}

        {/* Admin unlinked this word from a verb family — offer a way back. */}
        {isAdmin && !conjugationInfo && displayWord.conjugation_link_disabled && (
          <div className="rounded-2xl px-4 py-2.5 flex items-center justify-between gap-2"
               style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }}>
            <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
              Marked as not a conjugated form.
            </p>
            <button
              type="button"
              disabled={unlinkingConj}
              onClick={handleRestoreConjugation}
              className="dt-ghost-btn px-2.5 py-1 text-xs rounded-lg disabled:opacity-50 shrink-0"
              title="Let the automatic verb-form match apply to this word again"
            >
              {unlinkingConj ? "Restoring…" : "Restore link"}
            </button>
          </div>
        )}

        {/* ── Full family of the root verb (shown inline, no click needed) ── */}
        {conjugationInfo && rootFamily && (() => {
          const rs = (rootFamily.senses || []).find(
            (s) => s.past || s.present || s.imperative || s.masdar || s.ism_faail || s.ism_mafool
          );
          if (!rs) return null;
          const groups = [
            { label: "Past", key: "past", word: rs.past_word },
            { label: "Present", key: "present", word: rs.present_word },
            { label: "Imperative", key: "imperative", word: rs.imperative_word },
            { label: "Masdar", key: "masdar", word: rs.masdar_word },
            { label: "Active participle", key: "ism_faail", word: rs.ism_faail_word },
            { label: "Passive participle", key: "ism_mafool", word: rs.ism_mafool_word },
          ].filter((g) => rs[g.key]);
          if (!groups.length) return null;
          return (
            <div className="rounded-2xl px-4 py-3 space-y-1.5" style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--gold-dim)" }}>
                Forms of <span className="font-arabic" dir="rtl">{conjugationInfo.word_text}</span>
              </p>
              <div className="flex flex-wrap gap-2" dir="rtl">
                {groups.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => g.word && onFormWordClick && onFormWordClick(g.word.id, rs[g.key])}
                    disabled={!g.word}
                    className={`flex flex-col items-center rounded-xl border px-3 py-2 gap-0.5 transition-all bg-dt-forest ${
                      g.word ? "hover:border-dt-gold active:scale-95 cursor-pointer" : "opacity-60 cursor-default"
                    }`}
                    style={{ borderColor: "var(--panel-line)" }}
                  >
                    <span className="font-arabic text-base font-semibold" style={{ color: "var(--parchment)" }} dir="rtl">{rs[g.key]}</span>
                    <span className="text-[10px]" style={{ color: "var(--parchment-dim)" }}>{g.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Senses / definition ── */}
        <section className="surface-glass rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dt-line flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Definition</h3>
            {/* Admin fetch / re-fetch button */}
            {isAdmin && (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {!addingSense && (
                  <button
                    type="button"
                    onClick={() => setAddingSense(true)}
                    className="dt-ghost-btn px-2.5 py-1 text-xs rounded-lg"
                  >
                    + Add manually
                  </button>
                )}
                {isMissingDefinition ? (
                  <button
                    type="button"
                    onClick={() => handleFetchDefinition(false, "dict")}
                    disabled={fetchingDef}
                    className="px-2.5 py-1 text-xs rounded-lg bg-dt-gold text-dt-ink hover:brightness-105 disabled:opacity-50 flex items-center gap-1"
                  >
                    {fetchingDef && fetchingSource === "dict" ? (
                      <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75v1.5M17.127 6.873l-1.06 1.06M19.25 12h-1.5M17.127 17.127l-1.06-1.06M12 19.25v-1.5M6.873 17.127l1.06-1.06M4.75 12h1.5M6.873 6.873l1.06 1.06" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    )}
                    {fetchingDef && fetchingSource === "dict" ? "Fetching…" : "Fetch"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleFetchDefinition(true, "dict")}
                    disabled={fetchingDef}
                    className="px-2.5 py-1 text-xs rounded-lg bg-dt-gold text-dt-ink hover:brightness-105 disabled:opacity-50 flex items-center gap-1"
                  >
                    {fetchingDef && fetchingSource === "dict" ? (
                      <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75v1.5M17.127 6.873l-1.06 1.06M19.25 12h-1.5M17.127 17.127l-1.06-1.06M12 19.25v-1.5M6.873 17.127l1.06-1.06M4.75 12h1.5M6.873 6.873l1.06 1.06" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    {fetchingDef && fetchingSource === "dict" ? "Fetching…" : "Re-fetch"}
                  </button>
                )}
                {/* AI fetch (model dropdown + button) */}
                <div className="flex items-center gap-1">
                  <select
                    value={aiFetchModel}
                    onChange={(e) => {
                      const v = e.target.value;
                      setAiFetchModel(v);
                      try { localStorage.setItem("aiFetchModel", v); } catch {}
                    }}
                    disabled={fetchingDef}
                    title="AI model for AI fetch"
                    className="text-[10px] border border-slate-300 rounded px-1 py-0.5 bg-white text-slate-600 hover:border-violet-400 focus:outline-none focus:border-violet-500 disabled:opacity-50"
                  >
                    <option value="">Default</option>
                    <option value="gpt-5">GPT-5</option>
                    <option value="gpt-5-mini">GPT-5 Mini</option>
                    <option value="gpt-5-nano">GPT-5 Nano</option>
                    <option value="gpt-4.1">GPT-4.1</option>
                    <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => handleFetchDefinition(!isMissingDefinition, "ai")}
                    disabled={fetchingDef}
                    title={`Fetch from AI${aiFetchModel ? ` — ${aiFetchModel}` : ""}${isMissingDefinition ? "" : " (overwrite)"}`}
                    className="px-2.5 py-1 text-xs rounded-lg hover:brightness-105 disabled:opacity-50 flex items-center gap-1"
                    style={{ background: "linear-gradient(90deg, var(--gold), var(--gold-bright))", color: "var(--ink)" }}
                  >
                    <span className="text-sm leading-none">✨</span>
                    {fetchingDef && fetchingSource === "ai" ? "Fetching…" : isMissingDefinition ? "Fetch (AI)" : "Re-fetch (AI)"}
                  </button>
                </div>
              </div>
            )}
            {/* Learner self-service: get an AI definition for an undefined word
                in a topic they created (one-time per word). A conjugated form
                already carries its root verb's definition, so no fetch needed. */}
            {!isAdmin && topicOwnedByUser && isMissingDefinition && !conjDefinition && (
              <button
                type="button"
                onClick={handleUserFetchDefinition}
                disabled={userFetchingDef}
                className="px-2.5 py-1 text-xs rounded-lg hover:brightness-105 disabled:opacity-50"
                style={{ background: "linear-gradient(90deg, var(--gold), var(--gold-bright))", color: "var(--ink)" }}
              >
                {userFetchingDef ? "Fetching…" : "Get definition (AI)"}
              </button>
            )}
          </div>

          {fetchDefError && (
            <p className="px-4 py-2 text-xs text-dt-rose border-b border-dt-line" style={{ background: "var(--tag-rose-bg)" }}>{fetchDefError}</p>
          )}
          {fetchDefSuccess && (
            <p className="px-4 py-2 text-xs text-dt-teal border-b border-dt-line" style={{ background: "var(--tag-teal-bg)" }}>{fetchDefSuccess}</p>
          )}

          {displaySenses.length === 0 && !addingSense && !conjDefinition ? (
            <p className="px-4 py-4 text-sm text-dt-parchment-dim">No definition yet.</p>
          ) : null}

          {/* Inline manual definition form */}
          {addingSense && (
            <AddSenseForm
              wordId={displayWord.id}
              onAdded={(newSense) => {
                setLocalSenses((prev) => [...(prev || []), newSense]);
                setAddingSense(false);
              }}
              onCancel={() => setAddingSense(false)}
            />
          )}

          {/* Conjugation definition — reuse the root verb's definition for this
              form. Everyone sees it; only admins can adopt it as the default. */}
          {conjDefinition && (
            <div className="px-4 py-3 border-t border-dt-line flex items-start gap-1 bg-dt-forest-soft">
              <span className="text-sm text-dt-parchment flex-1 leading-relaxed italic">
                {conjugationInfo.definition}
                <span className="ml-1.5 text-xs text-dt-parchment-dim not-italic">
                  (from <span className="font-arabic">{conjugationInfo.word_text}</span>)
                </span>
              </span>
              {isAdmin && (() => {
                const hasPrimary = displaySenses.some(s => s.is_primary);
                const primaryMatchesConjugation = displaySenses.some(
                  s => s.is_primary && s.definition === conjugationInfo.definition
                );
                const isActive = !hasPrimary || primaryMatchesConjugation;
                return (
                  <button
                    type="button"
                    onClick={handleAdoptConjugationDefinition}
                    className={`shrink-0 transition-colors ml-1 text-base leading-none ${isActive ? "text-amber-400" : "text-slate-300 hover:text-amber-400"}`}
                    title={isActive ? "Current default (from conjugated form)" : "Set as default definition"}
                  >★</button>
                );
              })()}
            </div>
          )}

          {displaySenses.length > 0 && (
            <div className="divide-y divide-dt-line">
              {displaySenses.map((s) => (
                <div key={s.id} className="px-4 py-3 space-y-3">
                  {editingSenseId === s.id ? (
                    <SenseEdit
                      sense={s}
                      onSave={handleSenseSaved}
                      onCancel={() => setEditingSenseId(null)}
                    />
                  ) : (
                    <>
                      <div className="flex items-start gap-1">
                        {isAdmin ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditingSenseId(s.id)}
                              className="text-sm text-dt-parchment flex-1 leading-relaxed text-left hover:text-dt-gold-bright transition-colors"
                              title="Click to edit"
                            >
                              <SenseDefinitionText sense={s} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSetPrimary(s.id)}
                              className={`shrink-0 transition-colors ml-1 text-base leading-none ${s.is_primary ? "text-amber-400" : "text-slate-300 hover:text-amber-400"}`}
                              title={s.is_primary ? "Primary definition (shown in tooltip)" : "Set as primary definition"}
                            >
                              ★
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingVerbFormsSenseId(s.id)}
                              className="shrink-0 text-slate-300 hover:text-indigo-600 transition-colors ml-1 text-sm leading-none"
                              title="Add / edit / remove verb forms"
                            >
                              ✎
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSenseDeleted(s.id)}
                              className="shrink-0 text-slate-300 hover:text-red-500 transition-colors ml-1"
                              title="Delete definition"
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <p className="text-sm text-dt-parchment flex-1 leading-relaxed">
                            <SenseDefinitionText sense={s} />
                          </p>
                        )}
                      </div>

                      {s.usage_notes && (
                        <p className="text-xs text-dt-parchment-dim italic">{s.usage_notes}</p>
                      )}

                      {s.root && (
                        <div className="inline-flex items-center gap-1.5 rounded-lg bg-dt-forest-soft px-2.5 py-1">
                          <span className="text-xs text-dt-parchment-dim">Root</span>
                          <span className="font-arabic text-sm font-semibold text-dt-gold-bright">{s.root.pattern}</span>
                          {s.root.gloss && <span className="text-xs text-dt-parchment-dim">— {s.root.gloss}</span>}
                        </div>
                      )}

                      {/* ── Verb forms ── */}
                      {editingVerbFormsSenseId === s.id ? (
                        <VerbFormsEdit
                          sense={s}
                          onSave={handleSenseSaved}
                          onCancel={() => setEditingVerbFormsSenseId(null)}
                        />
                      ) : (
                        <>
                      {(() => {
                        // Gather all chips per type — primary + extras
                        const extrasByType = {};
                        (s.extra_forms || []).forEach((ex) => {
                          if (!extrasByType[ex.form_type]) extrasByType[ex.form_type] = [];
                          extrasByType[ex.form_type].push(ex);
                        });
                        const chipsFor = (key, word) => {
                          const out = [];
                          if (s[key]) {
                            out.push({
                              kind: "primary",
                              text: s[key],
                              word,
                              isDisplay: s.display_form === key,
                              onStar: () => handleSetDisplayForm(s.id, key),
                            });
                          }
                          (extrasByType[key] || []).forEach((ex) => {
                            out.push({
                              kind: "extra",
                              extraId: ex.id,
                              text: ex.text,
                              word: ex.word,
                              isDisplay: s.display_extra_form_id === ex.id,
                              onStar: () => handleSetDisplayExtraForm(s.id, ex.id),
                            });
                          });
                          return out;
                        };
                        const verbGroups = [
                          { label: "Past", ar: "ماضٍ", key: "past", word: s.past_word },
                          { label: "Present", ar: "مضارع", key: "present", word: s.present_word },
                          { label: "Imperative", ar: "أمر", key: "imperative", word: s.imperative_word },
                        ];
                        const nounGroups = [
                          { label: "Masdar", key: "masdar", word: s.masdar_word },
                          { label: "Active participle", key: "ism_faail", word: s.ism_faail_word },
                          { label: "Passive participle", key: "ism_mafool", word: s.ism_mafool_word },
                        ];
                        const hasVerbs = verbGroups.some(g => chipsFor(g.key, g.word).length);
                        const hasNouns = nounGroups.some(g => chipsFor(g.key, g.word).length);
                        return (
                          <>
                            {hasVerbs && (
                              <div className="space-y-1.5">
                                <p className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Verb forms</p>
                                <div className="flex flex-wrap gap-2" dir="rtl">
                                  {verbGroups.flatMap((g) =>
                                    chipsFor(g.key, g.word).map((chip, i) => (
                                      <div key={`${g.key}-${chip.kind}-${chip.extraId ?? "p"}-${i}`} className="relative">
                                        <button
                                          type="button"
                                          onClick={() => openParadigm(s.id, chip.text)}
                                          className={`flex flex-col items-center rounded-xl border bg-dt-forest-soft hover:border-dt-gold active:scale-95 transition-all px-3 py-2 gap-0.5 group ${
                                            chip.isDisplay ? "border-dt-gold ring-1 ring-dt-gold" : "border-dt-line"
                                          }`}
                                          title={`View full ${g.label.toLowerCase()} paradigm`}
                                        >
                                          <span className="font-arabic text-base font-semibold text-dt-parchment group-hover:text-dt-gold-bright" dir="rtl">
                                            {chip.text}
                                          </span>
                                          <span className="text-[10px] text-dt-parchment-dim group-hover:text-dt-gold">{g.label} · {g.ar}</span>
                                          <span className="text-[9px] text-dt-parchment-dim group-hover:text-dt-gold">tap for paradigm ▸</span>
                                        </button>
                                        {isAdmin && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); chip.onStar(); }}
                                            className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs leading-none flex items-center justify-center border shadow-sm ${
                                              chip.isDisplay
                                                ? "bg-amber-400 text-white border-amber-500"
                                                : "bg-white text-slate-300 border-slate-300 hover:text-amber-500 hover:border-amber-400"
                                            }`}
                                            title={chip.isDisplay ? "Currently shown in verb list — click to clear" : "Show this form in the topic verb list"}
                                          >★</button>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                            {hasNouns && (
                              <div className="space-y-1.5">
                                <p className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Derived nouns</p>
                                <div className="flex flex-wrap gap-1.5" dir="rtl">
                                  {nounGroups.flatMap((g) =>
                                    chipsFor(g.key, g.word).map((chip, i) => (
                                      <div key={`${g.key}-${chip.kind}-${chip.extraId ?? "p"}-${i}`} className="relative">
                                        <button
                                          type="button"
                                          className={`flex flex-col items-center rounded-xl border px-3 py-2 gap-0.5 bg-dt-forest-soft ${
                                            chip.word
                                              ? "hover:border-dt-gold active:scale-95 cursor-pointer"
                                              : "cursor-default"
                                          } ${
                                            chip.isDisplay ? "border-dt-gold ring-1 ring-dt-gold" : "border-dt-line"
                                          } transition-all group`}
                                          onClick={() =>
                                            chip.word && onFormWordClick && onFormWordClick(chip.word.id, chip.text)
                                          }
                                        >
                                          <span className="font-arabic text-base font-semibold text-dt-parchment group-hover:text-dt-gold-bright" dir="rtl">
                                            {chip.text}
                                          </span>
                                          <span className="text-[10px] text-dt-parchment-dim group-hover:text-dt-gold">{g.label}</span>
                                        </button>
                                        {isAdmin && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); chip.onStar(); }}
                                            className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs leading-none flex items-center justify-center border shadow-sm ${
                                              chip.isDisplay
                                                ? "bg-amber-400 text-white border-amber-500"
                                                : "bg-white text-slate-300 border-slate-300 hover:text-amber-500 hover:border-amber-400"
                                            }`}
                                            title={chip.isDisplay ? "Currently shown in verb list — click to clear" : "Show this form in the topic verb list"}
                                          >★</button>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                        </>
                      )}

                      {/* ── Examples ── */}
                      {s.examples && s.examples.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Examples</p>
                          <ul className="space-y-1">
                            {s.examples.map((ex) => (
                              <li key={ex.id} className="rounded-lg bg-dt-forest-soft border border-dt-line px-3 py-2">
                                <p className="text-sm font-arabic text-dt-parchment" dir="rtl">{ex.text}</p>
                                {ex.translation && (
                                  <p className="text-xs text-dt-parchment-dim mt-0.5">{ex.translation}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* ── Governing particles (حرف جر) ── */}
                      <ParticlesSection sense={s} isAdmin={isAdmin} />

                      {/* ── Dialect variants ── */}
                      {s.dialect_variants && s.dialect_variants.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Dialect variants</p>
                          <ul className="space-y-1">
                            {s.dialect_variants.map((dv) => (
                              <li key={dv.id} className="text-sm text-dt-parchment-dim">
                                <span className="font-medium text-dt-parchment">{dv.dialect}:</span>{" "}
                                <span className="font-arabic" dir="rtl">{dv.form}</span>
                                {dv.notes && <span className="text-dt-parchment-dim"> — {dv.notes}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
        {/* ── Plurals (only shown for non-plural words) ── */}
        {!isPlural && (
        <section className="surface-glass rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dt-line flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Plurals</h3>
            {isAdmin && !addingPlural && (
              <button
                type="button"
                onClick={() => { setAddingPlural(true); setEditingPluralId(null); }}
                className="dt-ghost-btn px-2.5 py-1 text-xs rounded-lg"
              >
                + Add
              </button>
            )}
          </div>
          <div className="px-4 py-3 space-y-2">
            {displayPlurals.length === 0 && !addingPlural && (
              <p className="text-sm text-dt-parchment-dim">No plurals recorded.</p>
            )}
            {displayPlurals.map((p) => (
              <div key={p.id}>
                {editingPluralId === p.id ? (
                  <PluralEdit
                    plural={p}
                    onSave={handlePluralSaved}
                    onCancel={() => setEditingPluralId(null)}
                  />
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    {(p.plural_word || p.resolved_plural_word) ? (
                      <button
                        type="button"
                        onClick={() => {
                          const w = p.plural_word || p.resolved_plural_word;
                          onFormWordClick && onFormWordClick(w.id, w.text);
                        }}
                        className="font-arabic text-base font-semibold text-dt-teal hover:underline"
                        dir="rtl"
                        title="Open plural word detail"
                      >
                        {p.form}
                      </button>
                    ) : (
                      <span className="font-arabic text-base font-semibold text-dt-parchment" dir="rtl">{p.form}</span>
                    )}
                    {p.plural_word && (
                      <span className="text-xs text-dt-teal border border-transparent rounded px-1.5 py-0.5" style={{ background: "var(--tag-teal-bg)" }}>linked</span>
                    )}
                    {!p.plural_word && p.resolved_plural_word && (
                      <span className="text-xs text-dt-slate border border-dt-line rounded px-1.5 py-0.5">auto</span>
                    )}
                    {p.transliteration && <span className="text-sm text-dt-parchment-dim">({p.transliteration})</span>}
                    {p.notes && <span className="text-sm text-dt-parchment-dim">— {p.notes}</span>}
                    {isAdmin && (
                      <span className="flex items-center ml-auto gap-0.5">
                        <PencilButton onClick={() => { setEditingPluralId(p.id); setAddingPlural(false); }} />
                        <TrashButton onClick={async () => {
                          if (!window.confirm(`Delete plural "${p.form}"?`)) return;
                          try {
                            await deletePlural(p.id);
                            handlePluralDeleted(p.id);
                          } catch (err) {
                            alert(err.message);
                          }
                        }} />
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            {addingPlural && (
              <AddPluralForm
                wordId={displayWord.id}
                onAdded={handlePluralAdded}
                onCancel={() => setAddingPlural(false)}
              />
            )}
          </div>
        </section>
        )} {/* end !isPlural */}

        {/* ── Singular form (shown for plural words, or for admin on non-singular words to mark as plural) ── */}
        {(isPlural || (isAdmin && displayPlurals.length === 0)) && (
          <section className="surface-glass rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-dt-line flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Singular form</h3>
              {/* Only allow linking a singular if this word isn't already a singular (has its own plurals) */}
              {isAdmin && displayPlurals.length === 0 && !addingSingular && (
                <button
                  type="button"
                  onClick={() => { setAddingSingular(true); setSingularSearchQ(""); setSingularSearchResults([]); }}
                  className="dt-ghost-btn px-2.5 py-1 text-xs rounded-lg"
                >+ Link singular</button>
              )}
            </div>
            <div className="px-4 py-3 space-y-2">
              {displaySingularOf.length === 0 && !addingSingular && (
                <p className="text-sm text-dt-parchment-dim">No singular linked.</p>
              )}
              {displaySingularOf.map((s) => (
                <div key={s.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onFormWordClick && onFormWordClick(s.id, s.text)}
                    className="font-arabic text-base font-semibold text-dt-teal hover:underline"
                    dir="rtl"
                    title="Open singular word detail"
                  >{s.text}</button>
                  {isAdmin && s.plural_record_id && (
                    <TrashButton onClick={async () => {
                      if (!window.confirm(`Remove singular link to "${s.text}"?`)) return;
                      try {
                        await deletePlural(s.plural_record_id);
                        setLocalSingularOf((prev) => (prev ?? displayWord.singular_of ?? []).filter((x) => x.id !== s.id));
                      } catch (err) { alert(err.message); }
                    }} />
                  )}
                </div>
              ))}
              {addingSingular && (
                <div className="space-y-2 pt-1">
                  <input
                    type="text"
                    autoFocus
                    value={singularSearchQ}
                    onChange={async (e) => {
                      const q = e.target.value;
                      setSingularSearchQ(q);
                      if (q.trim().length < 1) { setSingularSearchResults([]); return; }
                      setSingularSearchLoading(true);
                      try {
                        const res = await searchWords(q);
                        setSingularSearchResults(res.slice(0, 8));
                      } catch { setSingularSearchResults([]); }
                      finally { setSingularSearchLoading(false); }
                    }}
                    placeholder="Search for singular word…"
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-sky-400"
                    dir="rtl"
                  />
                  {singularSearchLoading && <p className="text-xs text-slate-400">Searching…</p>}
                  {singularSearchResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="block w-full text-right text-sm font-arabic px-3 py-1.5 rounded-lg hover:bg-sky-50 border border-transparent hover:border-sky-200"
                      dir="rtl"
                      onClick={async () => {
                        try {
                          await linkSingular(displayWord.id, r.id);
                          setLocalSingularOf((prev) => {
                            const base = prev ?? displayWord.singular_of ?? [];
                            if (base.some((x) => x.id === r.id)) return base;
                            return [...base, { id: r.id, text: r.text }];
                          });
                          setAddingSingular(false);
                          setSingularSearchQ("");
                          setSingularSearchResults([]);
                        } catch (err) { alert(err.message); }
                      }}
                    >{r.text}</button>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setAddingSingular(false); setSingularSearchQ(""); setSingularSearchResults([]); }}
                    className="text-xs text-slate-400 hover:text-slate-600"
                  >Cancel</button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Also appears in topics ── */}
        <section className="surface-glass rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dt-line">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Also in topics</h3>
          </div>
          <div className="px-4 py-3 space-y-1.5">
            {topicsForWordLoading && (
              <p className="text-sm text-dt-parchment-dim">Loading…</p>
            )}
            {topicsForWordError && (
              <p className="text-sm text-dt-rose">{topicsForWordError}</p>
            )}
            {!topicsForWordLoading && !topicsForWordError && (
              <>
                {(!topicsForWord || topicsForWord.length === 0) ? (
                  <p className="text-sm text-dt-parchment-dim">Only in the current topic.</p>
                ) : (
                  topicsForWord.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => onTopicSelect && onTopicSelect(t.id, t.name)}
                      className="w-full text-left px-3 py-2.5 min-h-[44px] rounded-xl border border-dt-line bg-dt-forest-soft hover:border-dt-gold text-sm text-dt-parchment transition-colors"
                    >
                      {t.name}
                    </button>
                  ))
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
